import React, { useState } from 'react';
import { redirectToCheckout } from '../services/stripeService';
import { useAuth } from '../contexts/AuthContext';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentSuccess: () => void;
  plan: 'standard' | 'pro' | 'pro_max';
}

const planDetails = {
  standard: {
    name: 'Standard',
    price: '¥500',
    description: '10枚（買い切り）',
    features: ['高解像度', '全機能利用可']
  },
  pro: {
    name: 'Pro',
    price: '¥1,000',
    description: '25枚（買い切り）',
    features: ['高解像度', '全機能利用可']
  },
  pro_max: {
    name: 'Pro Max',
    price: '¥5,000/月',
    description: '月200枚',
    features: ['高解像度', '全機能利用可']
  }
};

export const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onPaymentSuccess, plan }) => {
  const { user } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState<'stripe' | 'paypay' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planInfo = planDetails[plan];

  const handlePayment = async (method: 'stripe' | 'paypay') => {
    try {
      setIsProcessing(true);
      setError(null);
      setSelectedMethod(method);

      if (!user) {
        throw new Error('ユーザー情報が見つかりません');
      }

      if (method === 'stripe') {
        // Stripe決済（実際のStripe Checkoutにリダイレクト）
        console.log('🔄 Stripe決済開始:', { 
          plan, 
          userId: user.uid, 
          userEmail: user.email
        });
        
        await redirectToCheckout(plan, user.uid, user.email || '');
        // Stripe Checkoutにリダイレクトされるため、ここでは何もしない
      } else {
        // PayPay決済（今後実装）
        throw new Error('PayPay決済は現在準備中です');
      }
    } catch (error: any) {
      console.error('決済エラー:', error);
      
      // btoaエラーの特別な処理
      if (error.message && error.message.includes('btoa')) {
        setError('決済処理中にエラーが発生しました。しばらく時間をおいてから再度お試しください。');
      } else if (error.message && error.message.includes('InvalidCharacterError')) {
        setError('決済データの処理中にエラーが発生しました。しばらく時間をおいてから再度お試しください。');
      } else {
        setError(error.message || '決済に失敗しました。もう一度お試しください。');
      }
      
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            決済
          </h2>
          
          {/* プラン情報 */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-lg text-blue-900">{planInfo.name}</h3>
            <div className="text-2xl font-bold text-blue-600 my-2">
              {planInfo.price.includes('/月（税込）') ? (
                <>
                  {planInfo.price.replace('/月（税込）', '')}
                  <span className="text-lg font-normal">/月（税込）</span>
                </>
              ) : (
                planInfo.price
              )}
            </div>
            <p className="text-blue-700 text-sm">{planInfo.description}</p>
          </div>

          {/* 機能一覧 */}
          <div className="text-left mb-6">
            <h4 className="font-semibold text-slate-900 mb-2">含まれる機能</h4>
            <ul className="space-y-1">
              {planInfo.features.map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-slate-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* 決済方法選択 */}
          <div className="space-y-3">
            <button
              onClick={() => handlePayment('stripe')}
              disabled={isProcessing}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isProcessing && selectedMethod === 'stripe' ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  処理中...
                </div>
              ) : (
                'Stripeで決済'
              )}
            </button>


            <button
              onClick={onClose}
              disabled={isProcessing}
              className="w-full text-gray-500 hover:text-gray-700 transition-colors"
            >
              キャンセル
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
